import { Component, ViewChild, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { PrescriptionService } from 'src/core/services/prescription.service';
import { PresignedUrlService } from 'src/core/services/presigned-url.service';
import { PrescriptionUploadDialogComponent } from '../prescription-upload-dialog/prescription-upload-dialog.component';
import { ActivatedRoute, Router } from '@angular/router';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { Order } from 'src/interfaces/order';
import { formatDistance } from 'date-fns';
import { MatSort } from '@angular/material/sort';
import { environment } from 'src/environments/environment';
import { LocalStorageService } from 'src/core/services/local-storage.service';
import { Sort } from '@angular/material/sort';
import { OrderStatus } from 'src/interfaces/order-status';
import { UntypedFormBuilder } from '@angular/forms';
import { debounceTime } from 'rxjs/operators';
import { format } from 'date-fns';
import { sageSyncLabelFromOrder } from 'src/interfaces/sage-sync-status';

@Component({
    selector: 'app-prescription-list',
    templateUrl: './prescription-list.component.html',
    styleUrls: ['./prescription-list.component.scss'],
    standalone: false
})
export class PrescriptionListComponent implements OnInit {
  displayedColumns: string[] = [
    'id',
    'name',
    'status',
    'sage',
    'contact',
    'meds_assigned',
    'entities_assigned',
    'created_at',
    'updated_at',
    'view_prescription_file',
  ];

  ordersDataSource: MatTableDataSource<Order>;
  page: number = 1;
  limit: number = 10;
  totalNumberOfOrders: number = 0;
  orderStatuses: OrderStatus[] = [];
  selectedStatus: OrderStatus | null = null;
  sortDirection: string = 'desc';
  orderBy: string = 'created_at';

  orderFilterForm = this._formBuilder.group({
    status: [''],
    search: [''],
  });

  readonly acceptPrescriptionFiles = '.pdf';
  /** S3 folder order id → exists (null = checking) */
  private prescriptionFileByOrderId = new Map<number, boolean | null>();

  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  constructor(
    private prescriptionService: PrescriptionService,
    private router: Router,
    private route: ActivatedRoute,
    private presignedUrlService: PresignedUrlService,
    private localStorageService: LocalStorageService,
    private _formBuilder: UntypedFormBuilder,
    private _dialog: MatDialog
  ) {}
  ngOnInit(): void {
    if (!environment.sageUiEnabled) {
      this.displayedColumns = this.displayedColumns.filter((c) => c !== 'sage');
    }

    const limit = this.localStorageService.get('ORDERS', 'LIMIT');

    if (limit) {
      this.limit = parseInt(limit);
    }

    this.route.data.subscribe((data) => {
      this.ordersDataSource = new MatTableDataSource<Order>(data.orders.orders);
      this.orderStatuses = data.orders.extras.order_status;
      this.totalNumberOfOrders =
        this.prescriptionService.getTotalNumberOfOrders();
      this.ordersDataSource.sort = this.sort;
      this.page = data.orders.pageNumber;
      void this.refreshPrescriptionFileStatuses(data.orders.orders);
    });

    // Initialize search from query params (preserve search when returning from review)
    const initialSearch = this.route.snapshot.queryParamMap.get('search');
    if (initialSearch) {
      this.orderFilterForm.controls['search'].setValue(initialSearch, {
        emitEvent: false,
      });
      this.page = 1;
      this.listOrders(
        this.page,
        this.limit,
        this.sortDirection,
        this.selectedStatus ? this.selectedStatus.code : null,
        this.orderBy,
        initialSearch
      );
    }

    this.orderFilterForm.controls['status'].valueChanges.subscribe((value) => {
      this.page = 1;
      this.selectedStatus = value;
      this.listOrders(
        this.page,
        this.limit,
        this.sortDirection,
        this.selectedStatus ? this.selectedStatus.code : null,
        this.orderBy,
        this.orderFilterForm.value.search
      );
    });

    this.orderFilterForm.controls['search'].valueChanges
      .pipe(debounceTime(500))
      .subscribe((value) => {
        this.page = 1;
        this.listOrders(
          this.page,
          this.limit,
          this.sortDirection,
          this.selectedStatus ? this.selectedStatus.code : null,
          this.orderBy,
          value
        );
      });
  }

  resetStatus(event: Event) {
    event.stopPropagation();
    this.selectedStatus = null;
    this.orderFilterForm.patchValue({
      status: null,
    });
  }

  onPageChange(event: PageEvent) {
    // Get more orders
    this.page = event.pageIndex + 1;
    this.limit = event.pageSize;

    this.localStorageService.store('ORDERS', 'LIMIT', this.limit.toString());
    this.localStorageService.store('ORDERS', 'PAGE', this.page.toString());

    this.listOrders(
      this.page,
      this.limit,
      this.sortDirection,
      this.selectedStatus ? this.selectedStatus.code : null,
      this.orderBy,
      this.orderFilterForm.value.search
    );
  }

  listOrders(
    page: number = 1,
    limit: number = 10,
    order: string = 'desc',
    status: string | null = null,
    orderBy: string = 'created_at',
    search: string | null = null
  ) {
    (async () => {
      const newOrders = await this.prescriptionService.getOrders(
        page,
        limit,
        order,
        status,
        orderBy,
        search
      );
      const newData = [...newOrders];
      this.ordersDataSource.data = newData;
      this.totalNumberOfOrders =
        this.prescriptionService.getTotalNumberOfOrders();
      void this.refreshPrescriptionFileStatuses(newData);
    })();
  }

  getPrescriptionFileOrderId(order: Order): number {
    return order.parent_order_id ? order.parent_order_id : order.id;
  }

  private getPrescriptionS3Key(orderId: number): string {
    const { folder, fileName } = environment.s3;
    return `${folder}/${orderId}/${fileName}`;
  }

  async refreshPrescriptionFileStatuses(orders: Order[]): Promise<void> {
    const { region, bucket } = environment.s3;
    const orderIds = [...new Set(orders.map((o) => this.getPrescriptionFileOrderId(o)))];

    for (const id of orderIds) {
      this.prescriptionFileByOrderId.set(id, null);
    }

    await Promise.all(
      orderIds.map(async (id) => {
        const exists = await this.presignedUrlService.prescriptionFileExists(
          region,
          bucket,
          this.getPrescriptionS3Key(id)
        );
        this.prescriptionFileByOrderId.set(id, exists);
      })
    );
  }

  isPrescriptionFileChecking(order: Order): boolean {
    const status = this.prescriptionFileByOrderId.get(
      this.getPrescriptionFileOrderId(order)
    );
    return status === null || status === undefined;
  }

  prescriptionFileExists(order: Order): boolean {
    return (
      this.prescriptionFileByOrderId.get(this.getPrescriptionFileOrderId(order)) ===
      true
    );
  }

  openPrescriptionUploadDialog(event: Event, order: Order): void {
    event.stopPropagation();

    const orderId = this.getPrescriptionFileOrderId(order);
    const dialogRef = this._dialog.open(PrescriptionUploadDialogComponent, {
      width: '32rem',
      maxWidth: '95vw',
      data: {
        orderId,
        acceptFiles: this.acceptPrescriptionFiles,
      },
    });

    dialogRef.afterClosed().subscribe((uploaded) => {
      if (uploaded) {
        this.prescriptionFileByOrderId.set(orderId, true);
      }
    });
  }

  getSageSyncLabel(order: Order): string {
    return sageSyncLabelFromOrder(order);
  }

  getSageSyncClass(order: Order): string {
    const label = this.getSageSyncLabel(order).toLowerCase();
    if (label === 'synced' || label === 'sent') {
      return 'prescription-list__sage prescription-list__sage--synced';
    }
    if (label === 'failed') {
      return 'prescription-list__sage prescription-list__sage--failed';
    }
    if (label === 'pending') {
      return 'prescription-list__sage prescription-list__sage--pending';
    }
    return 'prescription-list__sage';
  }

  async sortOrders(sort: Sort) {
    switch (sort.active) {
      case 'status':
        this.orderBy = 'status_id';
        break;
      case 'created_at':
        this.orderBy = 'created_at';
        this.orderBy = 'created_at';
        break;
      case 'updated_at':
        this.orderBy = 'updated_at';
        break;

      default:
        this.orderBy = 'created_at';
        break;
    }
    this.sortDirection = sort.direction;
    this.listOrders(
      this.page,
      this.limit,
      this.sortDirection,
      this.selectedStatus ? this.selectedStatus.code : null,
      this.orderBy,
      this.orderFilterForm.value.search
    );
  }

  openPrescription(id: any) {
    // save the page I was on, so click back will bring back to this page
    this.router.navigate(['portal/prescriptions/review/', id], {
      queryParams: {
        search: this.orderFilterForm.value.search || undefined,
      },
    });
  }

  getOwnerTitle(order: Order) {
    if (order.patient) return order.patient.title;

    if (order.client) return order.client.name;

    if (order.doctor) return order.doctor.title;

    return '';
  }

  getEntityName(order: Order) {
    if (order.patient) {
      if (order.patient.company) return order.patient.company.name;
      return order.patient.first_name + ' ' + order.patient.last_name;
    }

    if (order.client) return order.client.name;

    if (order.doctor)
      return order.doctor.first_name + ' ' + order.doctor.last_name;

    return '';
  }

  getOwnerPhoneNumber(order: Order) {
    if (order.patient) return order.patient.phone_number;

    if (order.client) return order.client.phone_number;

    if (order.doctor) return order.doctor.phone_number;

    return '';
  }

  async openPrescriptionFile(event: Event, order: Order) {
    event.stopPropagation();

    const orderId = this.getPrescriptionFileOrderId(order);

    try {
      const { region, bucket } = environment.s3;
      const response = await this.presignedUrlService.generateDownloadUrl(
        region,
        bucket,
        this.getPrescriptionS3Key(orderId)
      );

      window.open(response.data.url, '_blank')?.focus();
    } catch (error) {
      console.error(error);

      return;
    }
  }

  getCreationTime(order: Order) {
    return format(new Date(order.created_at), 'MMMM dd, yyyy HH:mm');
  }

  formatTime(time: string | null) {
    if (!time) return '-';
    return format(new Date(time), 'MMMM dd, yyyy HH:mm');
  }

  isMedicineAssigned(order: Order): boolean {
    return order.number_of_items > 0;
  }

  isEntityAssigned(order: Order): boolean {
    return (
      order.patient !== null || order.doctor !== null || order.client !== null
    );
  }

  getBadgeBackground(status: OrderStatus) {
    switch (status.code) {
      case 'AWR': // Awaiting review
        return 'bg-dark';
      case 'AWF': // Awaiting fulfilment
        return 'bg-info';
      case 'PKG': // Packaging
        return 'bg-primary';
      case 'SHP': // Shipped
        return 'bg-success';

      default:
        return 'bg-secondary';
    }
  }
}
